import {
  Directive,
  HostBinding,
  HostListener,
  Input,
  inject,
} from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import {
  isTableRowNavIgnoredTarget,
  shouldOpenTableRowInNewTab,
} from "./table-row-nav.util";

@Directive({
  selector: "[appTableRowLink]",
  standalone: false,
})
export class TableRowLinkDirective {
  @Input("appTableRowLink") link:
    string | readonly unknown[] | null | undefined;
  @Input() appTableRowLinkQueryParams: Params | null = null;
  @Input() appTableRowLinkQueryParamsHandling:
    "merge" | "preserve" | "" | null = null;
  @Input() appTableRowLinkRelativeTo: ActivatedRoute | null = null;
  @Input() appTableRowLinkDisabled = false;

  private readonly router = inject(Router);

  @HostBinding("class.table-row--clickable")
  get clickable(): boolean {
    return this.hasLink && !this.appTableRowLinkDisabled;
  }

  @HostBinding("attr.data-table-row-link")
  get dataAttr(): string | null {
    return this.hasLink && !this.appTableRowLinkDisabled ? "" : null;
  }

  private get hasLink(): boolean {
    if (this.link == null) {
      return false;
    }
    if (typeof this.link === "string") {
      return this.link.trim().length > 0;
    }
    return this.link.length > 0;
  }

  @HostListener("click", ["$event"])
  onClick(event: MouseEvent): void {
    if (!this.canNavigate(event)) {
      return;
    }
    if (shouldOpenTableRowInNewTab(event)) {
      event.preventDefault();
      this.openInNewTab();
      return;
    }
    void this.router.navigateByUrl(this.createUrlTree());
  }

  @HostListener("auxclick", ["$event"])
  onAuxClick(event: MouseEvent): void {
    if (event.button !== 1 || !this.canNavigate(event)) {
      return;
    }
    event.preventDefault();
    this.openInNewTab();
  }

  private canNavigate(event: MouseEvent): boolean {
    if (!this.hasLink || this.appTableRowLinkDisabled) {
      return false;
    }
    if (event.defaultPrevented) {
      return false;
    }
    return !isTableRowNavIgnoredTarget(event.target);
  }

  private createUrlTree() {
    const commands =
      typeof this.link === "string" ? [this.link] : [...(this.link ?? [])];
    return this.router.createUrlTree(commands, {
      relativeTo: this.appTableRowLinkRelativeTo ?? undefined,
      queryParams: this.appTableRowLinkQueryParams ?? undefined,
      queryParamsHandling: this.appTableRowLinkQueryParamsHandling || undefined,
    });
  }

  private openInNewTab(): void {
    const url = this.router.serializeUrl(this.createUrlTree());
    window.open(url, "_blank", "noopener,noreferrer");
  }
}
